import {
    fetchProductById,
    getPriceFromMatrix,
    filterActiveAddons,
    calculateTotalPrice
} from '../services/productApi';
import { countLedSpots } from '../utils/ledUtils';

/**
 * New Veranda Calculator using matrix-based pricing from the backend
 */
class NewVerandaCalculator {
    constructor(formData, product) {
        this.formData = formData;
        this.product = product;
        this.width = parseFloat(formData.breedte) || 0;
        this.depth = parseFloat(formData.uitval) || 0;
    }

    /**
     * Fetch product data from the new API
     */
    static async fetchProductData(productId) {
        try {
            const product = await fetchProductById(productId);
            return product;
        } catch (error) {
            console.error('Error fetching product data:', error);
            throw error;
        }
    }

    /**
     * Get base product price
     */
    getBasePrice() {
        console.log('Getting base price...');
        console.log('Product:', this.product);
        console.log('Width:', this.width, 'Depth:', this.depth);

        if (!this.product) {
            console.warn('No product data available');
            return 0;
        }

        if (!this.product.pricing_data) {
            console.warn('Product has no pricing_data:', this.product);
            return 0;
        }

        console.log('Product pricing_data:', this.product.pricing_data);
        const price = getPriceFromMatrix(this.product.pricing_data, this.width, this.depth);
        console.log('Calculated base price:', price);

        return price;
    }

    /**
     * Get all active addons with pricing
     */
    getActiveAddons() {
        if (!this.product || !this.product.addons) {
            return [];
        }

        return filterActiveAddons(this.product.addons);
    }

    /**
     * Get price for a specific addon
     */
    getAddonPrice(addonId) {
        const addon = this.getActiveAddons().find(a => a.id === addonId);

        if (!addon || !addon.pricing_data) {
            return 0;
        }

        return getPriceFromMatrix(addon.pricing_data, this.width, this.depth);
    }

    /**
     * Get price for addon by type
     */
    getAddonPriceByType(addonType) {
        const addon = this.getActiveAddons().find(a => a.type === addonType);

        if (!addon || !addon.pricing_data) {
            return 0;
        }

        return getPriceFromMatrix(addon.pricing_data, this.width, this.depth);
    }

    /**
     * Calculate prices for selected addons based on form data
     */
    calculateSelectedAddonsPrices() {
        const addonPrices = {};
        const activeAddons = this.getActiveAddons();

        // Map form data to addon types
        const addonMappings = {
            // Front door — Note: 'Glazen Schuifdeur' is NOT here because it is
            // a Railssysteem schuifwand (Sliding Wall) and is priced below.
            'Aluminium Schuifdeur': 'Aluminium Door (Front)',

            // Side doors (left/right)
            'linker_wand_Aluminium Schuifdeur': 'Aluminium Door (Sides)',
            'linker_wand_Glazen Schuifdeur': 'Glass Door (Sides)',
            'rechter_wand_Aluminium Schuifdeur': 'Aluminium Door (Sides)',
            'rechter_wand_Glazen Schuifdeur': 'Glass Door (Sides)',

            // Other addons
            'zonwering_Ja': 'Zonwering',
            'linker_spie': 'Spie',
            'rechter_spie': 'Spie',
            'gutter_Rond': 'Gutter',
            'gutter_Klassiek': 'Gutter',
        };

        // Check front door
        if (this.formData.voorkant?.schuifdeur) {
            const frontDoorType = this.formData.voorkant.schuifdeur;
            const addonType = addonMappings[frontDoorType];

            if (addonType) {
                const price = this.getAddonPriceByType(addonType);
                if (price > 0) {
                    addonPrices[addonType] = price;
                }
            }
        }

        // Check left side
        if (this.formData.linker?.linker_wand_option && this.formData.linker.linker_wand_option !== 'Geen') {
            const leftWandType = `linker_wand_${this.formData.linker.linker_wand_option}`;
            const addonType = addonMappings[leftWandType];

            if (addonType) {
                const price = this.getAddonPriceByType(addonType);
                if (price > 0) {
                    addonPrices[`${addonType}_Left`] = price;
                }
            }
        }

        if (this.formData.linker?.linker_spie_option && this.formData.linker.linker_spie_option !== 'Geen') {
            const selectedName = this.formData.linker.linker_spie_option;
            const spieAddon = this.getActiveAddons().find(a => a.type === 'Spie' && a.name === selectedName);
            const price = spieAddon?.pricing_data ? getPriceFromMatrix(spieAddon.pricing_data, this.width, this.depth) : 0;
            if (price > 0) {
                addonPrices[`Spie_Left (${selectedName})`] = price;
            }
        }

        // Check right side
        if (this.formData.rechter?.rechter_wand_option && this.formData.rechter.rechter_wand_option !== 'Geen') {
            const rightWandType = `rechter_wand_${this.formData.rechter.rechter_wand_option}`;
            const addonType = addonMappings[rightWandType];

            if (addonType) {
                const price = this.getAddonPriceByType(addonType);
                if (price > 0) {
                    addonPrices[`${addonType}_Right`] = price;
                }
            }
        }

        if (this.formData.rechter?.rechter_spie_option && this.formData.rechter.rechter_spie_option !== 'Geen') {
            const selectedName = this.formData.rechter.rechter_spie_option;
            const spieAddon = this.getActiveAddons().find(a => a.type === 'Spie' && a.name === selectedName);
            const price = spieAddon?.pricing_data ? getPriceFromMatrix(spieAddon.pricing_data, this.width, this.depth) : 0;
            if (price > 0) {
                addonPrices[`Spie_Right (${selectedName})`] = price;
            }
        }

        // Check zonwering
        if (this.formData.zonwering?.zonwering_option === 'Ja') {
            const price = this.getAddonPriceByType('Zonwering');
            if (price > 0) {
                addonPrices['Zonwering'] = price;
            }
        }

        // Check gutter
        if (this.formData.gutter?.goot_option && this.formData.gutter.goot_option !== 'Geen') {
            const price = this.getAddonPriceByType('Gutter');
            if (price > 0) {
                addonPrices['Gutter'] = price;
            }
        }

        // Check for roof addon by name (since roof variants have same type but different names)
        if (this.formData.dak?.dak_option) {
            const selectedRoofName = this.formData.dak.dak_option;
            const roofAddon = this.getActiveAddons().find(a => a.name === selectedRoofName);

            if (roofAddon && roofAddon.pricing_data) {
                const price = getPriceFromMatrix(roofAddon.pricing_data, this.width, this.depth);
                if (price > 0) {
                    addonPrices[roofAddon.name] = price;
                }
            }
        }

        // Sliding Wall (Front) — Railssysteem schuifwanden pricing
        // Triggered when user selects 'Glazen Schuifdeur' as voorkant.
        // Rail count is encoded in the addon pricing matrix as width breakpoints:
        //   width 300 → 3 rails, 400 → 4 rails, 500 → 5 rails, 600 → 6 rails
        // Glass type is now explicitly selected via the right-side sub-options panel
        if (this.formData.voorkant?.schuifdeur === 'Glazen Schuifdeur') {
            const isGetint = this.formData.voorkant_glazen_schuifdeur_options?.glazen_schuifdeur_type === 'Getint Glas';
            const swName = isGetint ? 'Getint Glas' : 'Helder Glas';
            const swAddon = this.getActiveAddons().find(
                a => a.type === 'Sliding Wall (Front)' && a.name === swName
            );
            if (swAddon?.pricing_data) {
                const pd = {
                    ...swAddon.pricing_data,
                    depths: swAddon.pricing_data.depths || swAddon.pricing_data.heights,
                };
                const price = getPriceFromMatrix(pd, this.width, this.depth);
                if (price > 0) {
                    addonPrices['Glazen Schuifwanden'] = price;
                }
            }
        }

        // LED Verlichting — per-piece pricing: price_per_spot × total_spots
        if (this.formData.verlichting?.led_option === 'Ja') {
            const selectedPattern = this.formData.verlichting?.led_patroon_option;
            if (selectedPattern && selectedPattern !== 'Geen') {
                const ledAddon = this.getActiveAddons().find(
                    a => a.type === 'LED' && a.name === selectedPattern
                );
                if (ledAddon?.pricing_data) {
                    const pd = {
                        ...ledAddon.pricing_data,
                        depths: ledAddon.pricing_data.depths || ledAddon.pricing_data.heights,
                    };
                    const pricePerPiece = getPriceFromMatrix(pd, this.width, this.depth);
                    const totalSpots = countLedSpots(this.width, selectedPattern);
                    const totalLedPrice = pricePerPiece * totalSpots;
                    if (totalLedPrice > 0) {
                        addonPrices[`LED (${selectedPattern}) × ${totalSpots}`] = totalLedPrice;
                    }
                }
            }
        }

        return addonPrices;
    }

    /**
     * Calculate total price including base and all selected addons
     */
    calculateTotalPrice() {
        const basePrice = this.getBasePrice();
        const addonPrices = this.calculateSelectedAddonsPrices();

        const addonTotal = Object.values(addonPrices).reduce((sum, price) => sum + price, 0);
        const total = basePrice + addonTotal;

        return {
            basePrice: parseFloat(basePrice.toFixed(2)),
            addonPrices,
            addonTotal: parseFloat(addonTotal.toFixed(2)),
            total: parseFloat(total.toFixed(2)),
            breakdown: {
                base: {
                    name: this.product?.name || 'Base Product',
                    price: parseFloat(basePrice.toFixed(2))
                },
                addons: Object.entries(addonPrices).map(([name, price]) => ({
                    name,
                    price: parseFloat(price.toFixed(2))
                }))
            }
        };
    }

    /**
     * Get available colors for the product
     */
    getAvailableColors() {
        if (!this.product || !this.product.colors) {
            return [];
        }

        return this.product.colors.map(color => ({
            id: color.id,
            name: color.name,
            hexCode: color.hex_code
        }));
    }

    /**
     * Get product images
     */
    getProductImages() {
        if (!this.product || !this.product.images) {
            return [];
        }

        return this.product.images.map(image => ({
            id: image.id,
            url: image.url
        }));
    }

    /**
     * Get available dimensions from product pricing data
     */
    getAvailableDimensions() {
        if (!this.product || !this.product.pricing_data) {
            return { widths: [], depths: [] };
        }

        return {
            widths: this.product.pricing_data.widths || [],
            depths: this.product.pricing_data.depths || []
        };
    }

    /**
     * Validate if selected dimensions are available
     */
    validateDimensions() {
        const { widths, depths } = this.getAvailableDimensions();

        const isWidthValid = widths.length === 0 || widths.some(w => w >= this.width);
        const isDepthValid = depths.length === 0 || depths.some(d => d >= this.depth);

        return {
            isValid: isWidthValid && isDepthValid,
            isWidthValid,
            isDepthValid,
            selectedWidth: this.width,
            selectedDepth: this.depth,
            availableWidths: widths,
            availableDepths: depths
        };
    }
}

export default NewVerandaCalculator;
